import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCaretDown, faFilter } from '@fortawesome/free-solid-svg-icons';
import { Observable, Subject, map, of, startWith, tap } from 'rxjs';
import { View } from 'src/app/models/view';

@Component({
    selector: 'app-view-selector',
    templateUrl: './view-selector.component.html',
    styleUrls: ['./view-selector.component.scss'],
    imports: [FormsModule, FaIconComponent, MatInputModule, MatAutocompleteModule, ReactiveFormsModule, MatOptionModule, AsyncPipe]
})
export class ViewSelectorComponent implements OnInit, OnDestroy {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faFilter = faFilter;
    public faCaretDown = faCaretDown;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * Valeur indiquant si le controle de selection de la vue est visible.
     */
    @Input()
    visible: boolean;

    /**
     * Liste des vues selectionnables.
     */
    private _views: View[];

    /**
     * Obtient la liste des vues selectionnables.
     */
    @Input()
    set views(value: View[]) {
        this._views = value;
        this.filteredViews = of(value);
        this.defaultSelectedView = null;
        //if (this.selectFormControl) {
        //	// Si appellé avant l'initialisation peut être null.
        //	this.selectFormControl.setValue('');
        //}
    }

    /**
     * Définie la liste des vues selectionnables.
     */
    get views(): View[] {
        return this._views;
    }

    /**
     * Vue selectionnée par défaut.
     */
    private _defaultSelectedView: View;

    /**
     * Obtient la vue selectionnée par défaut.
     */
    @Input()
    set defaultSelectedView(value: View) {
        if (this.selectFormControl != null) {
            this.selectFormControl.setValue(value);
        }

        this._defaultSelectedView = value;
    }

    /**
     * Définie la vue selectionnée par défaut.
     */
    get defaultSelectedView(): View {
        return this._defaultSelectedView;
    }

    /**
     * Emis lorsque l'utilisateur selectionne une vue.
     */
    @Output()
    public viewSelected: EventEmitter<View> = new EventEmitter();

    /**
     * Vue selectionnée par l'utilisateur.
     */
    selectedView: View;

    /**
     * Vues affichées dans la liste déroulante.
     */
    filteredViews: Observable<View[]>;

    /**
     * Contrôle utilisé pour la saisie de la vue et l'affichage de la vue selectionnée.
     */
    selectFormControl: UntypedFormControl;

    /**
     * Constructeur.
     */
    constructor() {}

    /**
     * Initialise le composant.
     */
    ngOnInit() {
        console.log('ViewSelectorComponent --> ngOnInit');
        // Initialise le Contrôle utilisé pour la saisie de la vue.
        this.selectFormControl = new UntypedFormControl(this.defaultSelectedView);

        // S'abonne aux événements de saisie de l'utilisateur.
        this.filteredViews = this.selectFormControl.valueChanges.pipe(
            startWith(''),
            tap((value: string | View) => console.log('Change value : ', value)),
            map((value: string | View) => {
                if (typeof value === 'string') {
                    return this.filter((value as string) || '');
                } else {
                    this.viewSelected.emit(value as View);
                    return this.views;
                }
            })
        );
    }

    /**
     * Appellé par l'autocomplete pour déterminer le libellé à afficher pour une vue.
     * @param view : La vue à afficher
     * @returns : Le libellé de la vue.
     */
    displayLabel(view: View) {
        return view ? view.name : '';
    }

    /**
     * Détruit le composant.
     */
    ngOnDestroy() {
        console.log('ViewSelectorComponent --> ngOnDestroy');
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Appellée lorsque la liste déroulante se ferme.
     */
    public closed(): void {
        // Si le texte saisie ne correspond pas à une vue mais à du texte saisie par l'utilisateur.
        if (typeof this.selectFormControl.value === 'string') {
            // Réinitialise le champ avec vide.
            this.selectFormControl.setValue(this.defaultSelectedView);
        }
    }

    /**
     * Appellée lorsque la liste déroulante s'ouvre.
     */
    public opened(): void {
        // Réinitialise le champ à vide.
        this.selectFormControl.setValue('');
    }

    /**
     * Appellée lorsque l'utilisateur selectionne un element.
     */
    public selected(event: MatAutocompleteSelectedEvent): void {
        this.viewSelected.emit(event.option.value as View);
    }

    /**
     * Filtre les vues correspondant au texte saisie par l'utilisateur.
     * @param value : texte saisie par l'utilisateur.
     * @returns Les vues filtrées.
     */
    private filter(value: string): View[] {
        const filterValue = value.toLowerCase();
        return this.views.filter((view) => view.name.toLowerCase().includes(filterValue));
    }
}
